/**
 * Route guard that closes the client-side role boundary on dashboard
 * routes. Each role-scoped route group (owner / agm / coc / doctor)
 * in `App.tsx` is wrapped with `<RequireNavSection section="...">` so
 * a lower role hitting `/dashboard/owner` is redirected to their own
 * `me.homePath` BEFORE the role-specific page renders — meaning the
 * page's `useEffect` data fetch never fires, the backend never has
 * to 403, and the user never sees an owner URL flash in the address
 * bar with an empty/blocked card.
 *
 * Backend route auth in `api/routes/*` remains the real security
 * boundary; this guard is UX cleanliness only.
 *
 * Pure decision logic lives in `decideNavSectionAccess` in
 * `./role-view.ts` so it can be unit-tested without rendering React
 * Router; this file is the thin Router binding around it.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from './me-context';
import { decideNavSectionAccess } from './role-view';
import type { DashboardMe, DashboardNavSection } from './types';

interface RequireNavSectionViewProps {
  readonly section: DashboardNavSection;
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when the role has access. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/**
 * Pure presentational view — exported so tests can render it with
 * controlled props and `react-dom/server` without needing jsdom or a
 * live `/api/dashboard/me`. Mirrors the pattern used by
 * `DashboardLayoutView`.
 *
 * Returns `null` while loading so the protected page never renders
 * (and thus never fires its own data fetch) before the guard has
 * decided. Returns `<Navigate>` for both "no scope" and "wrong scope"
 * paths; no role-scoped UI is ever shown to a user who lacks the
 * section.
 */
export function RequireNavSectionView({
  section,
  loading,
  error,
  me,
  children,
}: RequireNavSectionViewProps): JSX.Element | null {
  const decision = decideNavSectionAccess(section, { loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireNavSection({
  section,
}: {
  section: DashboardNavSection;
}): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireNavSectionView
      section={section}
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
